import { FormEvent, memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import LoadingButton from '@/components/LoadingButton';

import { State } from './SessionTimeout.reducer';

interface Props {
  state: Pick<State, 'isLoading' | 'errorMessage' | 'password'>;
  isOpen: boolean;

  onSubmit: () => void;
  onLogout: () => void;
  onChangePassword: (password: string) => void;
}

const SessionTimeoutDialog = ({ state, isOpen, onSubmit, onLogout, onChangePassword }: Props) => {
  const { formatMessage } = useIntl();

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <>
      <Dialog
        open={isOpen}
        fullWidth
        maxWidth="xs"
        PaperProps={{ component: 'form', onSubmit: handleSubmit }}
      >
        <DialogTitle>
          <FormattedMessage defaultMessage="Resume session" />
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            <FormattedMessage defaultMessage="Your session has expired due to inactivity." />
          </Typography>
          <Typography>
            <FormattedMessage defaultMessage="Please enter your password to resume session or press the logout button to logout." />
          </Typography>
          <TextField
            autoFocus
            id="passphrase"
            name="passphrase"
            label={formatMessage({ defaultMessage: 'Password' })}
            type="password"
            required
            fullWidth
            value={state.password}
            onChange={(event) => onChangePassword(event.target.value)}
            autoComplete="off"
            error={!!state.errorMessage}
            helperText={state.errorMessage}
            disabled={state.isLoading}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onLogout} variant="outlined">
            <FormattedMessage defaultMessage="Logout" />
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={state.isLoading}
          >
            <FormattedMessage defaultMessage="Resume" />
          </LoadingButton>
        </DialogActions>
      </Dialog>
      {/* This element is used for reCAPTCHA (bound in QueryRecaptchaWidget on load) */}
      <div className="captcha" id="sessionTimeout-recaptcha" data-size="invisible" />
    </>
  );
};

export type { Props as SessionTimeoutDialogProps };
export default memo(SessionTimeoutDialog);
